/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { FC, KeyboardEvent, useState, useEffect, ChangeEvent } from 'react';
import { noop } from 'lodash';
import cx from 'classnames';

import { SvgIcon } from '@/components/SvgIcon';

import styles from './Input.module.sass';

export interface InputProps {
  id: string;
  name: string;
  type: 'text' | 'password';
  value: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onChange: (newValue: string) => void;
  onBlur?: () => void;
  maxLength?: number;
  className?: string;
  placeholder?: string;
  fullCopyableValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  floating?: boolean;
  copiable?: boolean;
  clearable?: boolean;
  isError?: boolean;
  autoComplete?: string;
}

const InputText: FC<InputProps> = (props) => {
  const {
    id,
    name,
    type,
    value,
    onKeyDown,
    onChange,
    onBlur = noop,
    maxLength,
    className = '',
    placeholder = '',
    fullCopyableValue = '',
    disabled = false,
    readOnly = false,
    required = false,
    autoFocus = false,
    floating = false,
    copiable = false,
    clearable = false,
    isError = false,
    autoComplete = '',
  } = props;

  const [revealed, setRevealed] = useState(false);
  const [tooltipText, setTooltipText] = useState<string>('');

  useEffect(() => {
    if (tooltipText) {
      const timeout = setTimeout(() => {
        setTooltipText('');
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [tooltipText]);

  const toggleHandler = () => {
    setRevealed((prev) => !prev);
  };

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const blurHandler = () => {
    onBlur();
  };

  const copyHandler = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      fullCopyableValue
        ? await navigator.clipboard.writeText(fullCopyableValue)
        : await navigator.clipboard.writeText(value);
      setTooltipText('Copied!');
    } catch (error: unknown) {
      // console.log(error);
      setTooltipText('Failed to copy!');
    }
  };

  const clearHandler = () => {
    onChange('');
  };

  const showPlaceholderAsLabel = floating && !!placeholder;
  const isPasswordInput = type === 'password';
  const showActions = isPasswordInput || copiable || clearable;
  const inputType = isPasswordInput && !revealed ? 'password' : 'text';

  const wrapperClassName = cx(
    className,
    styles.input,
    floating && styles.floating,
    clearable && !!value && styles.clearable,
    isError && styles.error,
  );

  const tooltipClassName = cx(
    styles.tooltip,
    !!tooltipText && styles.tooltip_active,
  );

  return (
    <div className={wrapperClassName}>
      <input
        id={id}
        name={name}
        type={inputType}
        className={styles.field}
        value={value}
        onChange={changeHandler}
        onKeyDown={onKeyDown}
        onBlur={blurHandler}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
      />

      {showPlaceholderAsLabel && (
        <label className={styles.label}>{placeholder}</label>
      )}

      {showActions && (
        <div className={styles.actions}>
          {clearable && !!value && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className={styles.action}
                onClick={clearHandler}
              >
                <SvgIcon name="close" />
              </button>
            </div>
          )}

          {copiable && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className={styles.action}
                onClick={copyHandler}
              >
                <SvgIcon name="copy" />
              </button>
              <div className={tooltipClassName}>{tooltipText}</div>
            </div>
          )}

          {isPasswordInput && (
            <button
              type="button"
              className={styles.action}
              onClick={toggleHandler}
            >
              <SvgIcon name={revealed ? 'show-password' : 'hide-password'} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InputText;
